import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';

import { useDashboardPath } from '#client/hooks/useDashboardPath';

import { BUSINESS_FINANCE_TAX_ACCOUNT_PARTNERS, TAX_ENTITLEMENTS } from '../../utils/taxUtils';

const ONE_800_A_ENTITLEMENTS = new Set([
  TAX_ENTITLEMENTS.TAX_UNLIMITED_ADVICE,
  TAX_ENTITLEMENTS.TAX_ANNUAL_FILING,
  TAX_ENTITLEMENTS.TAX_ANNUAL_FILING_PRIOR_YEAR,
]);

const APRIL_ENTITLEMENTS = new Set([
  TAX_ENTITLEMENTS.ZB_QUARTERLY_TAX_PLANNING,
  TAX_ENTITLEMENTS.ZB_TAX_FULL_SERVICE_BIZ_PER_LLC,
  TAX_ENTITLEMENTS.ZB_TAX_FULL_SERVICE_BIZ_PARTNERSHIP,
  TAX_ENTITLEMENTS.ZB_TAX_FULL_SERVICE_BIZ_S_CORP,
  TAX_ENTITLEMENTS.ZB_TAX_FULL_SERVICE_BIZ_C_CORP,
]);

/**
 * Component that checks if the business entity has tax partner exclusivity entitlements
 * and redirects to the appropriate page based on their exclusivity.
 * @param {object} props
 * @param {string} props.partnerName - The partner name for the current page ('1800accountant' or 'april')
 * @param {string} [props.redirectPath] - The absolute path to redirect to if the condition is met (will be processed by useDashboardPath)
 * @param {import('react').ReactNode} [props.children] - The children to render.
 * @component
 */
function TaxPartnerExclusivityGate({ partnerName, redirectPath = '/products', children }) {
  const dashboardRedirectPath = useDashboardPath(redirectPath);
  const { entitlements } = useBusinessEntity();
  const currentEntitlementKeys = Array.from(entitlements.keys());

  // Check which entitlement types the user has
  const hasOne800AEntitlement = currentEntitlementKeys.some((entitlement) => ONE_800_A_ENTITLEMENTS.has(entitlement));

  // Redirect if the user has the wrong entitlement for the current page
  if (hasOne800AEntitlement && partnerName !== BUSINESS_FINANCE_TAX_ACCOUNT_PARTNERS.ONE_800_ACCOUNTANT) {
    return <Navigate to={dashboardRedirectPath} replace />;
  }

  const hasAprilEntitlement = currentEntitlementKeys.some((entitlement) => APRIL_ENTITLEMENTS.has(entitlement));
  if (hasAprilEntitlement && partnerName !== BUSINESS_FINANCE_TAX_ACCOUNT_PARTNERS.APRIL) {
    return <Navigate to={dashboardRedirectPath} replace />;
  }

  // No tax partner exclusivity found, render the children
  return children;
}

TaxPartnerExclusivityGate.propTypes = {
  partnerName: PropTypes.oneOf(Object.values(BUSINESS_FINANCE_TAX_ACCOUNT_PARTNERS)).isRequired,
  redirectPath: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export default TaxPartnerExclusivityGate;
